import React, { ChangeEvent, useState } from "react";
import { Alert, Button, Form, Modal, ProgressBar, Table } from "react-bootstrap";
import {
  getBulkTemplate,
  getBulkTemplateUrl,
  TournamentBulkResource,
} from "../../config/tournamentBulkTemplates";
import { uploadFile } from "../../api/fileUploadService";
import {
  bulkUploadTournamentResource,
  normalizeBulkUploadResponse,
  parseBulkUploadIssue,
  TournamentBulkUploadResult,
} from "../../api/tournament/bulkUploadService";

interface TournamentBulkUploadModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess?: () => void;
  resource: TournamentBulkResource;
  tournamentId: string;
  tournamentSeasonId?: string;
  sport?: string;
}

const resourceLabels: Record<TournamentBulkResource, string> = {
  teams: "Teams",
  players: "Players",
  fixtures: "Fixtures",
  "player-stats": "Player Stats",
};

const isSpreadsheet = (file: File) => /\.(xlsx|xls)$/i.test(file.name);

const TournamentBulkUploadModal: React.FC<TournamentBulkUploadModalProps> = ({
  show,
  onHide,
  onSuccess,
  resource,
  tournamentId,
  tournamentSeasonId,
  sport,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<TournamentBulkUploadResult | null>(null);
  const template = getBulkTemplate(resource, sport);
  const templateUrl = getBulkTemplateUrl(resource, sport);

  const reset = () => {
    setFile(null);
    setError("");
    setResult(null);
  };

  const close = () => {
    reset();
    onHide();
  };

  const acknowledgeResult = () => {
    if (result?.successCount && (result.issues.length || result.unmatched.length)) {
      onSuccess?.();
    }
    close();
  };

  const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] || null;
    if (selected && !isSpreadsheet(selected)) {
      setFile(null);
      setError("Only .xlsx and .xls spreadsheet files are supported.");
      return;
    }
    setFile(selected);
    setError("");
  };

  const upload = async () => {
    if (!file || !tournamentSeasonId || !sport) {
      setError("A spreadsheet, tournament season, and sport are required.");
      return;
    }

    setUploading(true);
    setError("");
    try {
      const { key } = await uploadFile(file, "spreadsheets");
      const response = await bulkUploadTournamentResource({
        resource,
        tournamentId,
        tournamentSeasonId,
        sport,
        fileKey: key,
      });
      const uploadResult = normalizeBulkUploadResponse(response);
      setResult(uploadResult);

      if (!uploadResult.issues.length && !uploadResult.unmatched.length) {
        onSuccess?.();
      }
    } catch (err: any) {
      const uploadResult = normalizeBulkUploadResponse(err?.response?.data);
      if (uploadResult.issues.length || uploadResult.unmatched.length) {
        setResult(uploadResult);
      } else {
        setError(err?.message || "The spreadsheet could not be uploaded.");
      }
    } finally {
      setUploading(false);
    }
  };

  const label = resourceLabels[resource];

  return (
    <Modal show={show} onHide={uploading ? undefined : close} centered>
      <Modal.Header closeButton={!uploading}>
        <Modal.Title>Bulk Upload {label}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {result ? (
          <>
            <Alert variant={result.issues.length || result.unmatched.length ? "warning" : "success"}>
              {result.issues.length || result.unmatched.length
                ? "Upload completed with issues."
                : `${result.successCount} ${label.toLowerCase()} uploaded.`}
            </Alert>
            {result.issues.length > 0 && (
              <div className="mb-3">
                <h6 className="text-danger mb-2">
                  Upload could not be completed ({result.issues.length} issue{result.issues.length === 1 ? "" : "s"})
                </h6>
                <p className="text-muted mb-2">
                  Fix the listed spreadsheet rows, then upload the corrected file again.
                </p>
                <div className="border rounded" style={{ maxHeight: 360, overflowY: "auto" }}>
                  <Table responsive hover className="mb-0">
                    <thead className="sticky-top bg-light">
                      <tr>
                        <th>Tab</th>
                        <th>Row</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.issues.map((issue, index) => {
                        const parsedIssue = parseBulkUploadIssue(issue);

                        return (
                          <tr key={`${issue}-${index}`}>
                            <td>{parsedIssue.tab || "—"}</td>
                            <td>{parsedIssue.row || "—"}</td>
                            <td>{parsedIssue.message}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
            {result.unmatched.length > 0 && (
              <ul className="mb-0">
                {result.unmatched.map((item, index) => (
                  <li key={`${item.type}-${item.label}-${index}`}>
                    Unmatched {item.type}: {item.label}
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <>
            <p className="text-muted">
              Upload an Excel spreadsheet for this season. The selected sport is <strong>{sport || "not available"}</strong>.
            </p>
            {template && templateUrl ? (
              <a
                href={templateUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary mb-3"
              >
                Download {template.label}
              </a>
            ) : (
              <Alert variant="secondary">A template is not available for this sport.</Alert>
            )}
            <Form.Group controlId={`bulk-upload-${resource}`}>
              <Form.Label>Select spreadsheet</Form.Label>
              <Form.Control
                type="file"
                aria-label="Select spreadsheet"
                accept=".xlsx,.xls"
                onChange={selectFile}
                disabled={uploading}
              />
              {file && <Form.Text>{file.name}</Form.Text>}
            </Form.Group>
            {uploading && <ProgressBar animated now={50} label="Uploading…" className="mt-3" />}
            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {result ? (
          <>
            <Button variant="outline-secondary" onClick={reset}>Upload another</Button>
            <Button onClick={acknowledgeResult}>Done</Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={close} disabled={uploading}>Cancel</Button>
            <Button onClick={upload} disabled={!file || uploading}>
              {uploading ? "Uploading…" : "Upload"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default TournamentBulkUploadModal;
